import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AssignedUser,
  EventTypesResponse,
  LogEntryType,
  SystemLoggingEvent,
  SystemLoggingStats,
  TrendsResponse
} from '../../types/SystemLogging.types';

type SystemLoggingState = {
  events: SystemLoggingEvent[];
  total: number;
  loading: boolean;
  error: string | null;
  filters: {
    search: string;
    types: LogEntryType[];
    user: string[];
    dateRange: { startDate: string; endDate: string } | null;
  };
  pagination: {
    page: number;
    limit: number;
  };
  sorting: {
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  };
  eventTypes: EventTypesResponse | null;
  eventTypesLoading: boolean;
  eventTypesError: string | null;
  trends: TrendsResponse | null;
  trendsLoading: boolean;
  trendsError: string | null;
  stats: SystemLoggingStats | null;
  statsLoading: boolean;
  statsError: string | null;
  assignedUsers: AssignedUser[];
  assignedUsersLoading: boolean;
  assignedUsersError: string | null;
};

const initialState: SystemLoggingState = {
  events: [],
  total: 0,
  loading: false,
  error: null,
  filters: {
    search: '',
    types: [],
    user: [],
    dateRange: null
  },
  pagination: {
    page: 1,
    limit: 10
  },
  sorting: {
    sortBy: undefined,
    sortOrder: undefined
  },
  eventTypes: null,
  eventTypesLoading: false,
  eventTypesError: null,
  trends: null,
  trendsLoading: false,
  trendsError: null,
  stats: null,
  statsLoading: false,
  statsError: null,
  assignedUsers: [],
  assignedUsersLoading: false,
  assignedUsersError: null
};

const systemLoggingSlice = createSlice({
  name: 'systemLogging',
  initialState,
  reducers: {
    // Events
    setEventsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setEventsSuccess: (
      state,
      action: PayloadAction<{
        items: SystemLoggingEvent[];
        total: number;
        offset: number;
      }>
    ) => {
      state.events =
        action.payload.offset === 0
          ? action.payload.items
          : [...state.events, ...action.payload.items];
      state.total = action.payload.total;
      state.loading = false;
      state.error = null;
    },
    setEventsError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Event Types
    setEventTypesLoading: (state, action: PayloadAction<boolean>) => {
      state.eventTypesLoading = action.payload;
    },
    setEventTypesSuccess: (
      state,
      action: PayloadAction<EventTypesResponse>
    ) => {
      state.eventTypes = action.payload;
      state.eventTypesLoading = false;
      state.eventTypesError = null;
    },
    setEventTypesError: (state, action: PayloadAction<string>) => {
      state.eventTypesError = action.payload;
      state.eventTypesLoading = false;
    },

    // Trends
    setTrendsLoading: (state, action: PayloadAction<boolean>) => {
      state.trendsLoading = action.payload;
    },
    setTrendsSuccess: (state, action: PayloadAction<TrendsResponse>) => {
      state.trends = action.payload;
      state.trendsLoading = false;
      state.trendsError = null;
    },
    setTrendsError: (state, action: PayloadAction<string>) => {
      state.trendsError = action.payload;
      state.trendsLoading = false;
    },

    // Stats
    setStatsLoading: (state, action: PayloadAction<boolean>) => {
      state.statsLoading = action.payload;
    },
    setStatsSuccess: (state, action: PayloadAction<SystemLoggingStats>) => {
      state.stats = action.payload;
      state.statsLoading = false;
      state.statsError = null;
    },
    setStatsError: (state, action: PayloadAction<string>) => {
      state.statsError = action.payload;
      state.statsLoading = false;
    },

    // Assigned Users
    setAssignedUsersLoading: (state, action: PayloadAction<boolean>) => {
      state.assignedUsersLoading = action.payload;
    },
    setAssignedUsersSuccess: (state, action: PayloadAction<AssignedUser[]>) => {
      state.assignedUsers = action.payload;
      state.assignedUsersLoading = false;
      state.assignedUsersError = null;
    },
    setAssignedUsersError: (state, action: PayloadAction<string>) => {
      state.assignedUsersError = action.payload;
      state.assignedUsersLoading = false;
    },

    // Filters
    setSearchFilter: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
      state.pagination.page = 1;
      state.pagination.limit = 10; // Reset limit on filter change
      state.events = []; // Clear events on filter change
    },
    setTypesFilter: (state, action: PayloadAction<LogEntryType[]>) => {
      state.filters.types = action.payload;
      state.pagination.page = 1;
      state.pagination.limit = 10;
      state.events = [];
    },
    setUserFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.user = action.payload;
      state.pagination.page = 1;
      state.pagination.limit = 10;
      state.events = [];
    },
    setDateRangeFilter: (
      state,
      action: PayloadAction<{ startDate: string; endDate: string } | null>
    ) => {
      state.filters.dateRange = action.payload;
      state.pagination.page = 1;
      state.pagination.limit = 10;
      state.events = [];
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
      state.pagination.limit = 10;
      state.events = [];
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1;
    },
    setSorting: (
      state,
      action: PayloadAction<{ sortBy: string; sortOrder: 'asc' | 'desc' }>
    ) => {
      state.pagination.page = 1;
      state.sorting.sortBy = action.payload.sortBy;
      state.sorting.sortOrder = action.payload.sortOrder;
      state.events = [];
    },

    resetSystemLogging: () => initialState
  }
});

export const {
  setEventsLoading,
  setEventsSuccess,
  setEventsError,
  setEventTypesLoading,
  setEventTypesSuccess,
  setEventTypesError,
  setTrendsLoading,
  setTrendsSuccess,
  setTrendsError,
  setStatsLoading,
  setStatsSuccess,
  setStatsError,
  setAssignedUsersLoading,
  setAssignedUsersSuccess,
  setAssignedUsersError,
  setSearchFilter,
  setTypesFilter,
  setUserFilter,
  setDateRangeFilter,
  resetFilters,
  setPage,
  setLimit,
  setSorting,
  resetSystemLogging
} = systemLoggingSlice.actions;

export default systemLoggingSlice.reducer;
